import { useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { getSession } from "services/Session.services";
import { toast } from "sonner";

import type { Session } from "types/Session.type";

import { ROUTES, SESSION_STATUS } from "utils/constants";
import { queryClient } from "utils/queryClient";

import { useSubscriptionContext } from "context/Subscription";

export function useSessionDashboard() {
  const { id } = useParams<{ id: string }>();
  const { refetch } = useSubscriptionContext();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { subscription, isActive } = useSubscriptionContext();

  const { data, isLoading, isFetching, isError } = useGetSession(id as string);
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (!id) {
      navigate(ROUTES.HOME, { replace: true });
    }
  }, [id, navigate]);

  useEffect(() => {
    if (data?.sessionPatientId === null) {
      queryClient.invalidateQueries({ queryKey: ["session", id] });
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      navigate(ROUTES.HOME, { replace: true });

      toast.error(t("error.sessionDoesNotExist"));
      return;
    }
    if (data?.sessionStatusId === SESSION_STATUS.CREATED) {
      setStep(1);
    }
    if (data?.sessionStatusId === SESSION_STATUS.RECORDED) {
      setStep(2);
    }
    if (data?.sessionStatusId === SESSION_STATUS.COMPLETED) {
      setStep(2);
    }
  }, [data, id, navigate]);

  useEffect(() => {
    if (subscription && !isActive) {
      navigate(ROUTES.HOME, { replace: true });
    }
  }, [isActive, navigate, subscription]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (isError) {
      navigate(ROUTES.HOME, { replace: true });
    }
  }, [isError, navigate]);

  function useGetSession(id: string) {
    return useQuery<Session, Error>({
      queryKey: ["session", id],
      queryFn: () => getSession(id),
      enabled: !!id,
      staleTime: 0,
    });
  }

  return { step, isLoading, isFetching };
}
